import { useEffect, useState } from "react";
import { Trash2, Plus, KeyRound, Shield } from "lucide-react";
import StaffLayout from "@/components/staff/StaffLayout";
import { api } from "@/lib/api";
import { toast } from "sonner";

const PERM_LABELS = {
  rooms: "Rooms (CRUD, photos, iCal)",
  bookings: "Bookings (view, cancel, delete)",
  media: "Media library",
  content_blocks: "Content blocks (FAQ, surroundings…)",
  policies: "Policies",
  settings: "Site settings",
  reviews: "Reviews",
  messages: "Contact messages",
  users: "Staff users",
  payments: "Payments & payouts",
  partners: "Partners",
};

export default function StaffUsersPage() {
  const [users, setUsers] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState(null);
  const [permsFor, setPermsFor] = useState(null);

  function load() { api.get("/admin/users").then((r) => setUsers(r.data)); }
  useEffect(load, []);

  async function del(id) {
    if (!confirm("Remove this staff member?")) return;
    try {
      await api.delete(`/admin/users/${id}`);
      toast.success("Removed");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  }

  return (
    <StaffLayout>
      <div className="flex justify-between items-center flex-wrap gap-4">
        <div>
          <h1 className="font-display text-4xl">Staff users</h1>
          <p className="text-sm text-[#71717A] mt-1">Manage who can sign in to the admin portal. Toggle per-area access for each staff member.</p>
        </div>
        <button onClick={() => setShowAdd(true)} data-testid="staff-add-user" className="bg-brand-primary text-white px-5 py-2.5 rounded-sm text-sm flex items-center gap-2">
          <Plus size={14} /> Add staff
        </button>
      </div>

      <div className="mt-8 bg-white border border-[#E4E4E7] rounded-md overflow-x-auto">
        <table className="w-full text-sm min-w-[720px]">
          <thead className="bg-[#FAFAFA] text-[#71717A] text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left p-4">Name</th>
              <th className="text-left p-4">Email</th>
              <th className="text-left p-4">Role</th>
              <th className="text-left p-4">Access</th>
              <th className="text-left p-4">Created</th>
              <th></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#F4F4F5]">
            {users.map((u) => {
              const perms = u.permissions || {};
              const granted = Object.keys(PERM_LABELS).filter((k) => perms[k]).length;
              const total = Object.keys(PERM_LABELS).length;
              return (
                <tr key={u.id} data-testid={`staff-user-${u.id}`}>
                  <td className="p-4 font-medium">{u.name}</td>
                  <td className="p-4">{u.email}</td>
                  <td className="p-4">
                    <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded-sm ${u.role === "admin" ? "bg-brand-secondary text-brand-primary" : "bg-zinc-100 text-zinc-700"}`}>{u.role}</span>
                  </td>
                  <td className="p-4 text-xs text-[#71717A]">
                    {u.role === "admin" ? "Full access (admin)" : `${granted}/${total} areas`}
                  </td>
                  <td className="p-4 text-[#71717A] text-xs">{new Date(u.created_at).toLocaleDateString()}</td>
                  <td className="p-4 text-right whitespace-nowrap">
                    {u.role !== "admin" && (
                      <button data-testid={`perms-${u.id}`} onClick={() => setPermsFor(u)} className="p-2 hover:bg-[#FAFAFA] rounded-sm" title="Manage access"><Shield size={15} /></button>
                    )}
                    <button data-testid={`pwd-${u.id}`} onClick={() => setEditing(u)} className="p-2 hover:bg-[#FAFAFA] rounded-sm" title="Change password / role"><KeyRound size={15} /></button>
                    <button data-testid={`del-user-${u.id}`} onClick={() => del(u.id)} className="p-2 hover:bg-rose-50 text-rose-600 rounded-sm"><Trash2 size={15} /></button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {showAdd && <AddModal onClose={() => setShowAdd(false)} onCreated={() => { setShowAdd(false); load(); }} />}
      {editing && <EditModal user={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
      {permsFor && <PermsModal user={permsFor} onClose={() => setPermsFor(null)} onSaved={() => { setPermsFor(null); load(); }} />}
    </StaffLayout>
  );
}

function PermsModal({ user, onClose, onSaved }) {
  const initial = Object.fromEntries(
    Object.keys(PERM_LABELS).map((k) => [k, !!(user.permissions || {})[k]])
  );
  const [perms, setPerms] = useState(initial);

  async function save(e) {
    e.preventDefault();
    try {
      await api.put(`/admin/users/${user.id}/permissions`, { permissions: perms });
      toast.success("Access updated");
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  }

  function setAll(value) {
    setPerms(Object.fromEntries(Object.keys(PERM_LABELS).map((k) => [k, value])));
  }

  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <form onSubmit={save} className="bg-white w-full max-w-lg rounded-md max-h-[90vh] overflow-y-auto">
        <div className="p-5 border-b flex justify-between items-start sticky top-0 bg-white">
          <div>
            <h2 className="font-display text-2xl">Access for {user.name}</h2>
            <p className="text-xs text-[#71717A] mt-1">Toggle each area on or off. Admins always have full access.</p>
          </div>
          <div className="flex gap-2 text-xs">
            <button type="button" onClick={() => setAll(true)} className="px-3 py-1.5 border border-[#E4E4E7] rounded-sm hover:bg-[#FAFAFA]">All on</button>
            <button type="button" onClick={() => setAll(false)} className="px-3 py-1.5 border border-[#E4E4E7] rounded-sm hover:bg-[#FAFAFA]">All off</button>
          </div>
        </div>
        <div className="p-6 space-y-2">
          {Object.entries(PERM_LABELS).map(([key, label]) => (
            <div key={key} className="flex items-center justify-between py-2 border-b border-[#F4F4F5]">
              <span className="text-sm">{label}</span>
              <ToggleSwitch
                checked={!!perms[key]}
                onChange={(v) => setPerms({ ...perms, [key]: v })}
                testId={`perm-${key}`}
              />
            </div>
          ))}
        </div>
        <div className="p-5 border-t flex justify-end gap-3 sticky bottom-0 bg-white">
          <button type="button" onClick={onClose} className="px-5 py-2.5 text-sm">Cancel</button>
          <button data-testid="perms-save" type="submit" className="bg-brand-primary text-white px-6 py-2.5 rounded-sm text-sm">Save access</button>
        </div>
      </form>
    </div>
  );
}

export function ToggleSwitch({ checked, onChange, testId }) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      data-testid={testId}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${checked ? "bg-brand-primary" : "bg-zinc-300"}`}
    >
      <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${checked ? "translate-x-6" : "translate-x-1"}`} />
    </button>
  );
}

function AddModal({ onClose, onCreated }) {
  const [f, setF] = useState({ email: "", name: "", password: "", role: "staff" });
  async function submit(e) {
    e.preventDefault();
    try {
      await api.post("/admin/users", f);
      toast.success("Staff added");
      onCreated();
    } catch (e2) { toast.error(e2.response?.data?.detail || "Failed"); }
  }
  return (
    <Modal title="Add staff member" onClose={onClose} onSave={submit} saveLabel="Add">
      <Field label="Name"><input data-testid="new-user-name" required value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="staff-i" /></Field>
      <Field label="Email"><input data-testid="new-user-email" required type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="staff-i" /></Field>
      <Field label="Temporary password"><input data-testid="new-user-pwd" required type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className="staff-i" /></Field>
      <Field label="Role">
        <select value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} className="staff-i">
          <option value="staff">Staff</option>
          <option value="admin">Admin</option>
        </select>
      </Field>
      <p className="text-xs text-[#71717A]">New staff start with all areas enabled. Manage access with the shield icon after creation.</p>
    </Modal>
  );
}

function EditModal({ user, onClose, onSaved }) {
  const [f, setF] = useState({ name: user.name, role: user.role, password: "" });
  async function submit(e) {
    e.preventDefault();
    try {
      const payload = { name: f.name, role: f.role };
      if (f.password) payload.password = f.password;
      await api.put(`/admin/users/${user.id}`, payload);
      toast.success("Saved");
      onSaved();
    } catch (e2) { toast.error(e2.response?.data?.detail || "Failed"); }
  }
  return (
    <Modal title={`Edit ${user.name}`} onClose={onClose} onSave={submit} saveLabel="Save">
      <Field label="Name"><input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="staff-i" /></Field>
      <Field label="Role">
        <select value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })} className="staff-i">
          <option value="staff">Staff</option>
          <option value="admin">Admin</option>
        </select>
      </Field>
      <Field label="New password (leave blank to keep)"><input type="password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} className="staff-i" /></Field>
    </Modal>
  );
}

function Modal({ title, onClose, onSave, saveLabel, children }) {
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <form onSubmit={onSave} className="bg-white w-full max-w-md rounded-md">
        <div className="p-5 border-b">
          <h2 className="font-display text-2xl">{title}</h2>
        </div>
        <div className="p-6 space-y-4 text-sm">{children}</div>
        <style>{`.staff-i{width:100%;border:1px solid #E4E4E7;border-radius:.125rem;padding:.5rem .75rem;font-size:.875rem;background:#fff}`}</style>
        <div className="p-5 border-t flex justify-end gap-3">
          <button type="button" onClick={onClose} className="px-5 py-2.5 text-sm">Cancel</button>
          <button type="submit" data-testid="modal-save" className="bg-brand-primary text-white px-6 py-2.5 rounded-sm text-sm">{saveLabel}</button>
        </div>
      </form>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-wider text-[#71717A]">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
